import React from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryParams } from '../../../providers/QueryParamsProvider';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import { FILTERS_PAGE_SIZE } from '../filters-request';
import { deleteSavedFilter, getFiltersByTable } from '../../../../_services';

export const useFilterState = (
  tableId: any,
  { fetchFilterData, deleteFilter, onNoFilters }: any = {}
) => {
  const { query, replace } = useQueryParams();
  const { pathname } = useLocation();
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const { data: allFilters } = useQuery(
    [`${tableId}-tableFilters`],
    () => getFilters(),
    {
      keepPreviousData: true,
      staleTime: 300000,
      refetchOnWindowFocus: false,
    }
  );

  const tableFilterOptions = React.useMemo(
    () => (Array.isArray(allFilters) ? allFilters.filter((f) => !f.chip) : []),
    [allFilters]
  );
  const systemFilters = React.useMemo(
    () => (Array.isArray(allFilters) ? allFilters.filter((f) => f.system) : []),
    [allFilters]
  );
  const chips = React.useMemo(
    () => (Array.isArray(allFilters) ? allFilters.filter((f) => f.chip) : []),
    [allFilters]
  );

  const getFilters = React.useCallback(() => {
    if (typeof fetchFilterData === 'function') {
      return fetchFilterData();
    }

    return getFiltersByTable({ table: tableId, size: FILTERS_PAGE_SIZE })
      .then(({ data }: any) => {
        if (data.content) {
          return data.content.map((f: any) => ({
            label: f.name,
            value: f.id,
            filter: f.filter.replace(/filter/g, 'f'),
            system: f.system,
            chip: f.chip,
          }));
        }
      })
      .catch((err) => {
        notifications.error(
          'Sorry there was a problem fetching your filters, please try again in a few minutes.'
        );

        throw err;
      });
  }, [notifications, fetchFilterData, tableId]);

  const handleDeleteFilter = React.useCallback(
    (option: any) => {
      if (typeof deleteFilter === 'function') {
        return deleteFilter(option.value);
      }

      return (event: any) => {
        event.preventDefault();
        event.stopPropagation();

        deleteSavedFilter({ id: option.value })
          .then(() => {
            notifications.success('Filter removed');
            queryClient.invalidateQueries([`${tableId}-tableFilters`]);
          })
          .catch(() => {
            notifications.error('Failed to remove filter');
          });
      };
    },
    // eslint-disable-next-line
    [allFilters]
  );

  const handleNoFilters = React.useCallback(() => {
    const { f, fn, page, searchAfter, pageRequested, ...rest } = query;
    if (typeof onNoFilters === 'function') {
      return onNoFilters();
    }
    replace(`${pathname}`, { query: rest });
    // eslint-disable-next-line
  }, [query]);

  const filterProps = React.useMemo(
    () => ({
      id: tableId,
      allFilters,
      tableFilterOptions,
      systemFilters,
      chips,
      getFilters,
      handleDeleteFilter,
      handleNoFilters,
    }),
    // eslint-disable-next-line
    [allFilters, query]
  );

  return filterProps;
};
